/**
 * Finding the product inside a photo.
 *
 * Background removal returns the subject on a blank field but keeps whatever framing
 * the photographer used, and `applyColourAndRatio` then pads MORE blank space around it
 * to reach 4:5. Measured 2026-08-25 across 530 live products: the product occupied a
 * median 62% of its own frame, worst cases 35%, which is why storefront cards rendered
 * a stamp on a white field.
 *
 * Pure functions only — no canvas, no DOM. The canvas wrapper lives in imageProcessing.js
 * so this logic stays testable under jsdom, which has no canvas implementation.
 */

/** Alpha at or above this counts as subject, when the image has real transparency. */
const OPAQUE_MIN_ALPHA = 16

/** Per-channel difference from the sampled background that counts as subject. */
const BACKGROUND_TOLERANCE = 24

/** At or above this on every channel, a flat image is blank rather than a subject. */
const NEAR_WHITE_MIN = 240

/**
 * Bounding box of the subject within an ImageData-shaped object.
 *
 * @param {{data: Uint8ClampedArray|number[], width: number, height: number}} image
 * @returns {{left: number, top: number, right: number, bottom: number}|null}
 *   Inclusive pixel bounds, or null when the image is blank and should be left alone.
 */
export function findContentBounds(image) {
    const { width, height } = image
    if (!width || !height) return null

    const background = sampleCorners(image)
    const isSubject = background.transparent
        ? (px) => px.a >= OPAQUE_MIN_ALPHA
        : (px) => px.a >= OPAQUE_MIN_ALPHA && differsFrom(px, background)

    const bounds = scanFor(image, isSubject)
    if (bounds) return bounds

    // Nothing stood out from the corners. Either the image is blank, or the subject
    // covers every pixel including the corners we sampled.
    return isBlank(background) ? null : fullFrame(width, height)
}

/**
 * Grow a subject box by a margin, without running outside the frame.
 *
 * The margin is a fraction of the subject's LONG edge and is applied equally to both
 * axes, so a wide product and a tall one get visually comparable breathing room.
 *
 * @param {{left: number, top: number, right: number, bottom: number}} bounds
 * @param {{width: number, height: number}} frame
 * @param {number} margin Fraction of the long edge, e.g. 0.04 for 4%.
 */
export function boundsWithMargin(bounds, frame, margin) {
    const subjectWidth = bounds.right - bounds.left + 1
    const subjectHeight = bounds.bottom - bounds.top + 1
    const pad = Math.round(Math.max(subjectWidth, subjectHeight) * margin)

    return {
        left: Math.max(0, bounds.left - pad),
        top: Math.max(0, bounds.top - pad),
        right: Math.min(frame.width - 1, bounds.right + pad),
        bottom: Math.min(frame.height - 1, bounds.bottom + pad),
    }
}

/**
 * Whether all four corners are blank (transparent or near-white): a cutout on an empty
 * field. Each corner is judged on its own, so one dark corner is enough to say no.
 *
 * @param {{data: Uint8ClampedArray|number[], width: number, height: number}} image
 */
export function hasBlankCorners(image) {
    if (!image.width || !image.height) return false
    return cornerPixels(image).every(isBlankPixel)
}

/** Whether one pixel is blank (transparent or near-white). */
export function isBlankAt(image, x, y) {
    return isBlankPixel(pixelAt(image, x, y))
}

function scanFor(image, isSubject) {
    const { width, height } = image
    let left = width, top = height, right = -1, bottom = -1

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (!isSubject(pixelAt(image, x, y))) continue
            if (x < left) left = x
            if (x > right) right = x
            if (y < top) top = y
            if (y > bottom) bottom = y
        }
    }

    return right < 0 ? null : { left, top, right, bottom }
}

/**
 * The background colour, averaged over the four corners. Averaging rather than trusting
 * one pixel keeps JPEG noise from shifting the reference.
 */
function sampleCorners(image) {
    const corners = cornerPixels(image)
    const mean = (key) => Math.round(corners.reduce((sum, px) => sum + px[key], 0) / corners.length)

    return {
        r: mean('r'), g: mean('g'), b: mean('b'),
        transparent: corners.every((px) => px.a < OPAQUE_MIN_ALPHA),
    }
}

function cornerPixels(image) {
    const { width, height } = image
    return [
        pixelAt(image, 0, 0),
        pixelAt(image, width - 1, 0),
        pixelAt(image, 0, height - 1),
        pixelAt(image, width - 1, height - 1),
    ]
}

function isBlankPixel(px) {
    return px.a < OPAQUE_MIN_ALPHA
        || (px.r >= NEAR_WHITE_MIN && px.g >= NEAR_WHITE_MIN && px.b >= NEAR_WHITE_MIN)
}

function differsFrom(px, background) {
    return Math.abs(px.r - background.r) > BACKGROUND_TOLERANCE
        || Math.abs(px.g - background.g) > BACKGROUND_TOLERANCE
        || Math.abs(px.b - background.b) > BACKGROUND_TOLERANCE
}

/** A uniform frame is blank only if it is transparent or near-white, not merely flat. */
function isBlank(background) {
    return background.transparent
        || (background.r >= NEAR_WHITE_MIN && background.g >= NEAR_WHITE_MIN && background.b >= NEAR_WHITE_MIN)
}

function fullFrame(width, height) {
    return { left: 0, top: 0, right: width - 1, bottom: height - 1 }
}

function pixelAt(image, x, y) {
    const i = (y * image.width + x) * 4
    return { r: image.data[i], g: image.data[i + 1], b: image.data[i + 2], a: image.data[i + 3] }
}
